// 🔐 accounts (09-30, ACCOUNTS_SIGNIN_SPEC_2026_09_30.md §3.8 + the 09-30 look
// book §5): /account. Your spaces (made first, with the name you go by there),
// your AIs, where you're signed in, sign out, sign out everywhere, delete.
// Every room and AI has a Remove (two taps; the account lets go, the device keeps it).
// While sign-in is off the route sends you to /explore, like before it existed.
// In the /explore C look, dark first (utils/themeChoice).
import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ACCOUNT_CSS } from '../auth/accountStyles';
import { useAccount } from '../auth/useAccount';
import { refreshAccount, signOut, deleteAccount, removeFromAccount, maskEmail, initialOf } from '../auth/accountState';
import { showAccountToast } from '../auth/accountEvents';
import SignInCard, { savedLine } from '../auth/SignInCard';
import { signInOn } from '../auth/signInSwitch';
import { prefersDark } from '../utils/themeChoice';

const PAGE_CSS = `
.ms-page{min-height:100vh;min-height:100dvh;background:var(--bg);padding:0 clamp(16px,4vw,40px) 64px}
.ms-page-top{height:62px;display:flex;align-items:center;gap:12px;border-bottom:1px solid var(--line);margin:0 calc(-1 * clamp(16px,4vw,40px)) 28px;padding:0 clamp(16px,4vw,40px)}
.ms-wm{font-weight:300;font-size:24px;letter-spacing:-.01em;line-height:1;text-decoration:none}
.ms-wm i{font-style:normal;font-weight:600;color:var(--green)}
.ms-body{max-width:880px;margin:0 auto;display:grid;gap:24px}
.ms-head{display:flex;align-items:center;gap:14px;flex-wrap:wrap}
.ms-big{position:relative;display:grid;place-items:center;width:46px;height:46px;border-radius:50%;background:var(--on);font-weight:600;font-size:18px;flex:none}
.ms-big::after{content:"";position:absolute;right:-1px;bottom:-1px;width:11px;height:11px;border-radius:50%;background:var(--green);box-shadow:0 0 0 2px var(--bg)}
.ms-head h1{margin:0;font-weight:500;font-size:24px;letter-spacing:-.01em}
.ms-head small{color:var(--muted);font-family:"Geist Mono",ui-monospace,monospace;font-size:12.5px}
.ms-cols{display:grid;gap:24px;grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr))}
.ms-list{display:grid;gap:2px;align-content:start}
.ms-list h2{margin:0 0 6px;font:500 11px/1 "Geist Mono",ui-monospace,monospace;letter-spacing:.09em;text-transform:uppercase;color:var(--muted);padding:0 10px}
.ms-li{display:grid;grid-template-columns:38px minmax(0,1fr) auto;gap:12px;align-items:center;padding:9px 10px;border-radius:12px;text-decoration:none}
a.ms-li:hover{background:var(--hover)}
.ms-tile{width:38px;height:38px;border-radius:10px;display:grid;place-items:center;background:var(--on);font-weight:600;overflow:hidden}
.ms-tile img{width:100%;height:100%;object-fit:cover}
.ms-tile.ai{border-radius:50%;box-shadow:0 0 0 2px color-mix(in srgb,var(--cyan) 55%,transparent),0 0 14px -2px color-mix(in srgb,var(--cyan) 60%,transparent)}
.ms-li-t{display:grid;gap:1px;min-width:0}
.ms-li-t b{font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ms-li-t small{color:var(--muted);font-size:12.5px}
.ms-pill{font:500 10.5px/1 "Geist Mono",ui-monospace,monospace;letter-spacing:.06em;text-transform:uppercase;padding:5px 7px;border-radius:6px;background:var(--on);color:var(--muted);white-space:nowrap}
.ms-pill.made{color:var(--green)}
.ms-empty{color:var(--muted);font-size:14px;padding:6px 10px}
.ms-actions{display:flex;flex-wrap:wrap;gap:10px;align-items:center;border-top:1px solid var(--line);padding-top:18px}
.ms-btn{height:36px;display:inline-flex;align-items:center;padding:0 14px;border-radius:9px;box-shadow:inset 0 0 0 1px var(--line);font-weight:600;font-size:13.5px}
.ms-btn:hover{background:var(--hover)}
.ms-btn.danger{color:var(--danger);box-shadow:inset 0 0 0 1px color-mix(in srgb,var(--danger) 45%,transparent)}
.ms-btn[disabled]{opacity:.5;cursor:default}
.ms-grow{flex:1}
.ms-confirm{display:grid;gap:10px;padding:14px;border-radius:12px;box-shadow:inset 0 0 0 1px color-mix(in srgb,var(--danger) 45%,transparent)}
.ms-confirm p{margin:0;font-size:14px;color:var(--muted)}
.ms-confirm-row{display:flex;flex-wrap:wrap;gap:10px}
.ms-confirm input{height:36px;min-width:0;flex:1 1 160px;border-radius:9px;border:0;box-shadow:inset 0 0 0 1px var(--line);background:var(--bg);color:var(--ink);padding:0 12px;font:inherit;font-size:16px}
.ms-center{display:grid;place-items:center;padding-top:6vh}
.ms-note{color:var(--muted);font-size:14px;margin:0}
.ms-row{display:flex;align-items:center;gap:2px;min-width:0}
.ms-row > .ms-li{flex:1;min-width:0}
.ms-rm{flex:none;height:30px;display:inline-flex;align-items:center;padding:0 10px;border-radius:8px;color:var(--muted);font-size:12.5px}
.ms-rm:hover{background:var(--hover);color:var(--ink)}
.ms-rm.sure{color:var(--danger)}
`;

/** "Remove", then "Remove?" for 4 seconds: one stray tap never drops a room. */
function RemoveButton({ label, onRemove }) {
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!sure) return undefined;
    const t = setTimeout(() => setSure(false), 4000);
    return () => clearTimeout(t);
  }, [sure]);
  return (
    <button
      type="button"
      className={sure ? 'ms-rm sure' : 'ms-rm'}
      disabled={busy}
      aria-label={sure ? `Remove ${label} from your account, are you sure?` : `Remove ${label} from your account`}
      onClick={async () => {
        if (!sure) { setSure(true); return; }
        setBusy(true);
        await onRemove();
        setBusy(false);
        setSure(false);
      }}
    >
      {sure ? 'Remove?' : 'Remove'}
    </button>
  );
}

function ago(when) {
  const t = new Date(when).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 120) return 'Just now';
  if (s < 3600) return `${Math.round(s / 60)} minutes ago`;
  if (s < 86400) { const h = Math.round(s / 3600); return h === 1 ? 'An hour ago' : `${h} hours ago`; }
  const d = Math.round(s / 86400);
  return d === 1 ? 'Yesterday' : `${d} days ago`;
}

const since = (when) => {
  const d = new Date(when);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

export default function AccountPage() {
  const account = useAccount();
  const navigate = useNavigate();
  const [dark] = useState(prefersDark);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (signInOn()) document.title = 'Your account · myspaice'; }, []);
  useEffect(() => { if (account.status === 'signed_in') refreshAccount(); }, [account.status]);
  useEffect(() => {
    if (account.status === 'signed_in' && window.location.hash === '#ais') {
      const el = document.getElementById('ais');
      if (el) el.scrollIntoView({ block: 'start' });
    }
  }, [account.status]);

  const s = account.summary || {};
  const rooms = s.rooms || [];
  const agents = s.agents || [];
  const sessions = s.sessions || [];
  const email = account.account && account.account.email;

  const doSignOut = async (everywhere) => {
    setBusy(true);
    setError(null);
    const r = await signOut({ everywhere });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; } // "everywhere" didn't happen: say so, stay signed in to retry
    showAccountToast(everywhere ? 'Signed out everywhere.' : 'Signed out.');
    navigate('/explore');
  };
  const doRemove = async (kind, id, name) => {
    setError(null);
    const r = await removeFromAccount({ kind, id });
    if (!r.ok) { setError(r.error); return; }
    showAccountToast(`${name} is off your account.`);
  };

  if (!signInOn()) return <Navigate to="/explore" replace />;
  const doDelete = async () => {
    if (typed.trim().toLowerCase() !== 'delete' || busy) return;
    setBusy(true);
    setError(null);
    const r = await deleteAccount();
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    showAccountToast('Your account is deleted.');
    navigate('/explore');
  };

  return (
    <div className="ms-acct ms-page" data-mode={dark ? 'dark' : 'light'}>
      <style>{ACCOUNT_CSS + PAGE_CSS}</style>
      <style>{`html,body{background:${dark ? '#0D0F13' : '#F6F7F9'};}`}</style>
      <header className="ms-page-top">
        <Link className="ms-wm" to="/explore">myspa<i>i</i>ce</Link>
      </header>
      <main className="ms-body">
        {account.status === 'off' && (
          <div className="ms-center"><p className="ms-note">Sign-in isn't open yet. <Link to="/explore">Back to the spaces</Link></p></div>
        )}
        {account.status === 'loading' && <p className="ms-note" role="status">One moment…</p>}
        {account.status === 'signed_out' && (
          <div className="ms-center">
            <SignInCard clientId={account.googleClientId} dark={dark} onDone={(r) => showAccountToast(savedLine(r))} />
          </div>
        )}
        {account.status === 'signed_in' && (
          <>
            <div className="ms-head">
              <span className="ms-big" aria-hidden="true">{initialOf(email)}</span>
              <div>
                <h1>Your account</h1>
                <small className="ph-no-capture">{maskEmail(email)}{account.account && account.account.created_at ? ` · since ${since(account.account.created_at)}` : ''}</small>
              </div>
            </div>

            <div className="ms-cols">
              <section className="ms-list" aria-labelledby="ms-spaces">
                <h2 id="ms-spaces">Your spaces</h2>
                {rooms.length === 0 && <p className="ms-empty">Rooms you visit or make show up here.</p>}
                {rooms.map((r) => (
                  <div key={r.id} className="ms-row">
                    <Link className="ms-li" to={`/game-step?gameId=${r.id}`}>
                      <span className="ms-tile" aria-hidden="true">{String(r.name || '?').trim()[0] || '?'}</span>
                      <span className="ms-li-t"><b>{r.name || 'A room'}</b><small>{r.seat_name ? `You're ${r.seat_name}` : 'Your room'}</small></span>
                      <span className={r.made ? 'ms-pill made' : 'ms-pill'}>{r.made ? 'You made it' : 'Visited'}</span>
                    </Link>
                    <RemoveButton label={r.name || 'this room'} onRemove={() => doRemove('room', r.id, r.name || 'That room')} />
                  </div>
                ))}
              </section>

              <div className="ms-list">
                <section className="ms-list" aria-labelledby="ms-ais" id="ais">
                  <h2 id="ms-ais">Your AIs</h2>
                  {agents.length === 0 && <p className="ms-empty">AIs you bring in show up here.</p>}
                  {agents.map((a) => (
                    <div key={a.id} className="ms-row">
                      <Link className="ms-li" to={`/agents/${a.id}`}>
                        <span className="ms-tile ai" aria-hidden="true">{a.avatar_url ? <img src={a.avatar_url} alt="" /> : '✦'}</span>
                        <span className="ms-li-t"><b>{a.name || 'Your AI'}</b><small>Yours</small></span>
                        <span />
                      </Link>
                      <RemoveButton label={a.name || 'this AI'} onRemove={() => doRemove('agent', a.id, a.name || 'That AI')} />
                    </div>
                  ))}
                </section>
                <section className="ms-list" aria-labelledby="ms-devices" style={{ marginTop: 18 }}>
                  <h2 id="ms-devices">Signed in on</h2>
                  {sessions.map((x, i) => (
                    <div key={i} className="ms-li">
                      <span className="ms-tile" aria-hidden="true">{/iOS|Android/.test(x.client) ? '📱' : '💻'}</span>
                      <span className="ms-li-t"><b>{x.client}</b><small>{x.current ? 'This device' : ago(x.last_seen_at)}</small></span>
                      <span />
                    </div>
                  ))}
                </section>
              </div>
            </div>

            <div className="ms-actions">
              <button type="button" className="ms-btn" disabled={busy} onClick={() => doSignOut(false)}>Sign out</button>
              <button type="button" className="ms-btn" disabled={busy} onClick={() => doSignOut(true)}>Sign out everywhere</button>
              <span className="ms-grow" />
              {!confirming && <button type="button" className="ms-btn danger" disabled={busy} onClick={() => setConfirming(true)}>Delete account</button>}
            </div>
            {error && !confirming && <p className="ms-acct-error" role="alert">{error}</p>}
            {confirming && (
              <form className="ms-confirm" onSubmit={(e) => { e.preventDefault(); doDelete(); }}>
                <p>Your email and sign-ins are deleted. Your seats stay in their rooms like any visitor's. Type delete to confirm.</p>
                <div className="ms-confirm-row">
                  <input id="ms-delete-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="delete" autoComplete="off" aria-label="Type delete to confirm" />
                  <button type="submit" className="ms-btn danger" disabled={busy || typed.trim().toLowerCase() !== 'delete'}>Delete account</button>
                  <button type="button" className="ms-btn" onClick={() => { setConfirming(false); setTyped(''); }}>Keep it</button>
                </div>
                {error && <p className="ms-acct-error" role="alert">{error}</p>}
              </form>
            )}
          </>
        )}
      </main>
    </div>
  );
}
